import { AfterViewInit, Component, computed, ElementRef, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { DatePipe, NgStyle } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { AbstractDialog, AbstractDialogData, DialogComponent } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { SatelliteImage } from 'src/app/tga/shared/models/models';
import { toLocaleSignal } from 'src/app/shared/utils/locale.utils';
import { TgaSatelliteImagesDialogStore } from './tga-satellite-images-dialog.signal-store';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NDVI_GRADIENT } from 'src/app/tga/shared/constants/tga.constants';
import { DataUriPipe } from 'src/app/tga/shared/pipes/data-uri.pipe';

export interface ImagePopUpDialogData extends AbstractDialogData {
  images: SatelliteImage[];
  initialIndex: number;
  cropFieldId: number;
  minDate: Date;
  maxDate: Date;
  initialFetchFromDate?: string;
}

@Component({
  selector: 'app-image-pop-up-dialog',
  standalone: true,
  providers: [TgaSatelliteImagesDialogStore],
  imports: [
    DatePipe,
    NgStyle,
    MatIconButton,
    MatIcon,
    TranslatePipe,
    DialogComponent,
    MatProgressSpinner,
    DataUriPipe,
  ],
  templateUrl: './image-pop-up-dialog.component.html',
  styleUrls: ['./image-pop-up-dialog.component.scss'],
})
export class ImagePopUpDialogComponent extends AbstractDialog implements AfterViewInit, OnDestroy {
  protected readonly mdDialogRef = inject<MatDialogRef<ImagePopUpDialogComponent>>(MatDialogRef);
  readonly data = inject<ImagePopUpDialogData>(MAT_DIALOG_DATA);

  private readonly localeService = inject(LocaleService);
  private readonly dialogStore = inject(TgaSatelliteImagesDialogStore);

  @ViewChild('imageWrapper') private readonly imageWrapperRef!: ElementRef<HTMLDivElement>;
  @ViewChild('thumbnailContainer') private readonly thumbnailContainerRef!: ElementRef<HTMLDivElement>;
  private wheelHandler: ((e: WheelEvent) => void) | null = null;
  private scrollToThumbnailTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    super();
    this.dialogStore.initialize(
      this.data.cropFieldId,
      this.data.images,
      this.data.initialFetchFromDate ?? new Date().toISOString(),
    );
  }
  protected readonly images = this.dialogStore.sortedImages;
  protected readonly isLoadingMore = this.dialogStore.isLoadingMore;

  protected readonly currentIndex = signal(this.data.initialIndex);
  protected readonly currentImage = computed(() => this.images()[this.currentIndex()]);
  protected readonly canGoToPrevious = computed(() => this.currentIndex() > 0);
  protected readonly canGoToNext = computed(() => this.currentIndex() < this.images().length - 1);
  protected readonly zoomLevel = signal(1);
  protected readonly panX = signal(0);
  protected readonly panY = signal(0);
  protected readonly imageTransform = computed(
    () => `translate(${this.panX()}px, ${this.panY()}px) scale(${this.zoomLevel()})`,
  );
  protected readonly gradientStyle = computed(() => ({ background: NDVI_GRADIENT }));
  protected readonly locale = toLocaleSignal(this.localeService);
  protected readonly selectedDate = signal<Date | null>(null);

  protected readonly displayedDate = computed(() => this.selectedDate() ?? new Date(this.currentImage().imageDate));

  protected readonly hasImageForDisplayedDate = computed(() => {
    const d = this.displayedDate();
    const key = d.toLocaleDateString(this.locale());
    return this.imageDateSet().has(key);
  });

  protected readonly imageForDisplayedDate = computed(() => {
    const d = this.displayedDate();
    const key = d.toLocaleDateString(this.locale());
    return this.images().find((img) => {
      const di = new Date(img.imageDate);
      const ik = di.toLocaleDateString(this.locale());
      return ik === key;
    });
  });

  private static readonly MAX_GAP_DAYS = 28;

  protected readonly canFetchMore = computed(() => {
    const oldest = new Date(this.dialogStore.oldestImageDate());
    const fetchedFrom = new Date(this.dialogStore.fetchedFromDate());
    const gapDays = (oldest.getTime() - fetchedFrom.getTime()) / (1000 * 60 * 60 * 24);
    return oldest > this.data.minDate && !this.dialogStore.isLoadingMore() && gapDays <= ImagePopUpDialogComponent.MAX_GAP_DAYS;
  });

  private readonly imageDateSet = computed(() => {
    const set = new Set<string>();
    for (const img of this.images()) {
      try {
        const d = new Date(img.imageDate);
        const key = d.toLocaleDateString(this.locale());
        set.add(key);
      } catch {
        // in case of invalid date, skip adding to set
      }
    }
    return set;
  });

  protected isDragging = false;
  private lastPointerX = 0;
  private lastPointerY = 0;
  private hasPanned = false;

  ngAfterViewInit(): void {
    this.wheelHandler = (event: WheelEvent) => {
      event.preventDefault();
      if (event.deltaY < 0) {
        this.onZoomIn();
      } else {
        this.onZoomOut();
      }
    };
    this.imageWrapperRef?.nativeElement.addEventListener('wheel', this.wheelHandler, { passive: false });
    this.scrollToActiveThumbnail();
  }

  ngOnDestroy(): void {
    if (this.wheelHandler && this.imageWrapperRef?.nativeElement) {
      this.imageWrapperRef.nativeElement.removeEventListener('wheel', this.wheelHandler);
    }
    if (this.scrollToThumbnailTimeoutId !== null) {
      globalThis.clearTimeout(this.scrollToThumbnailTimeoutId);
    }
  }

  protected onThumbnailClicked(index: number): void {
    this.currentIndex.set(index);
    this.selectedDate.set(null);
    this.resetZoomAndPan();
  }

  protected onStripPrevious(): void {
    if (this.canGoToPrevious()) {
      this.currentIndex.update((i) => i - 1);
      this.selectedDate.set(null);
      this.resetZoomAndPan();
      this.scrollToActiveThumbnail();
    } else {
      this.tryFetchMoreBefore();
    }
  }

  protected onStripNext(): void {
    if (this.canGoToNext()) {
      this.currentIndex.update((i) => i + 1);
      this.selectedDate.set(null);
      this.resetZoomAndPan();
      this.scrollToActiveThumbnail();
    }
  }

  protected onPointerDown(event: PointerEvent): void {
    this.isDragging = true;
    this.lastPointerX = event.clientX;
    this.lastPointerY = event.clientY;
    this.hasPanned = false;
    (event.target as Element)?.setPointerCapture?.(event.pointerId);
  }

  protected onPointerMove(event: PointerEvent): void {
    if (!this.isDragging) return;

    if (this.zoomLevel() > 1) {
      const dx = event.clientX - this.lastPointerX;
      const dy = event.clientY - this.lastPointerY;
      this.panX.update((x) => x + dx);
      this.panY.update((y) => y + dy);
      this.lastPointerX = event.clientX;
      this.lastPointerY = event.clientY;
      this.hasPanned = true;
    }
  }

  protected onPointerUp(event: PointerEvent): void {
    if (!this.isDragging) return;
    (event.target as Element)?.releasePointerCapture?.(event.pointerId);

    if (!this.hasPanned) {
      const delta = this.lastPointerX - event.clientX;
      if (delta > 50) {
        this.onStripNext();
      } else if (delta < -50) {
        this.onStripPrevious();
      }
    }
    this.isDragging = false;
  }

  protected onZoomIn(): void {
    this.zoomLevel.update((z) => Math.min(z + 0.25, 3));
  }

  protected onZoomOut(): void {
    this.zoomLevel.update((z) => {
      const next = Math.max(z - 0.25, 1);
      if (next === 1) {
        this.panX.set(0);
        this.panY.set(0);
      }
      return next;
    });
  }

  private resetZoomAndPan(): void {
    this.zoomLevel.set(1);
    this.panX.set(0);
    this.panY.set(0);
  }

  private scrollToActiveThumbnail(): void {
    this.scrollToThumbnailTimeoutId = globalThis.setTimeout(() => {
      const container = this.thumbnailContainerRef?.nativeElement;
      if (!container) return;
      const activeEl = container.querySelector('.thumbnail-item.active') as HTMLElement;
      if (!activeEl) return;
      activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      this.scrollToThumbnailTimeoutId = null;
    });
  }

  private tryFetchMoreBefore(): void {
    if (!this.canFetchMore()) return;

    const currentImg = this.currentImage();
    const fetchBefore = new Date(this.dialogStore.oldestImageDate());

    this.dialogStore.fetchMoreBefore(fetchBefore, this.data.minDate).subscribe(() => {
      // Rebase currentIndex to keep showing the same image after prepend
      if (currentImg) {
        const newIndex = this.images().findIndex((img) => img.imageDate === currentImg.imageDate);
        if (newIndex >= 0) {
          this.currentIndex.set(newIndex);
        }
      }
      this.scrollToActiveThumbnail();
    });
  }
}

